import { useEffect, useMemo, useRef, useState } from "react";
import { submitAbnHold } from "../utils/api";

/* ============================================================================
 * HoldPaymentsModal
 * ----------------------------------------------------------------------------
 * Opened from SubbiePayments (only while the "Payments" chip is active).
 * Shows the 3 live totals (PO Value / GST / Inc GST) for the Payments rows,
 * then a table grouped by End Of Month. Ticking a row removes its amounts
 * from the cyan totals (and shows them in the amber "Held" pills) and marks
 * it HPN; Submit flips the ticked records to HPN via recordId so the .aba
 * run skips them. Unticking adds the amounts straight back.
 *
 * Visual language mirrors ComplianceExpiryModal — all styling inline,
 * same backdrop / 880px shell / gradient accent, no horizontal scrolling
 * (PO Num + Approved columns dropped to give the rest room).
 *
 * Props:
 *   rows     – Payments-chip rows (raw index arrays from fetchABNPayments)
 *   setToast – FilterTemplate's toast setter
 *   onClose  – () => void
 *   refetch  – () => Promise  — reload SubbiePayments after submit
 * ==========================================================================*/

/* ---------- row indexes (same map as SubbiePayments) ---------- */
const IDX = {
  id: 0, email: 1, endOfMonth: 2, status: 3, poNum: 4,
  poValue: 5, gst: 6, incGst: 7, business: 8, recordId: 9,
  bankName: 10, bsb: 11, accountNum: 12, incGstRaw: 13, recordId1: 14,
};

const HPN_STATUS = "HPN"; // must match backend HPN_STATUS

/* ---------- helpers ---------- */
const parseCurrency = (s) => {
  if (typeof s === "number") return s;
  const n = parseFloat(String(s ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) =>
  `$${Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Airtable synced-table settle: the PO table this app reads is a sync of the
 * subbie source we patch, and the sync delay varies. Instead of a fixed
 * sleep, poll the refetch until the held records actually drop off the
 * Payments data (or give up after MAX_POLLS). */
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 10; // ~25s worst case before giving up politely
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

/* ---------- styles (mirrors ComplianceExpiryModal) ---------- */
const S = {
  backdrop: {
    position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(2,6,23,0.7)", zIndex: 1100,
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "40px 16px", overflowY: "auto",
  },
  modal: {
    position: "relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width: "100%", maxWidth: 880,
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    border: "1px solid rgba(34,211,238,0.25)", borderRadius: 16,
    padding: "18px 18px 14px",
  },
  panelAccent: {
    position: "absolute", top: 0, left: 0, right: 0, height: 3,
    background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)",
  },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 },
  title: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#f1f5f9" },
  closeX: {
    background: "none", border: "none", color: "rgba(255,255,255,0.6)",
    fontSize: "1.4rem", lineHeight: 1, cursor: "pointer",
  },
  eyebrow: {
    fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase",
    color: "#22d3ee", fontWeight: 700,
  },
  /* totals pills — cyan = will be paid, amber = held (HPN) */
  totalsRow: { display: "flex", gap: 8, marginBottom: 8 },
  totalPill: (held) => ({
    flex: "1 1 0", minWidth: 0, textAlign: "center", borderRadius: 12, padding: "10px 10px",
    background: held ? "rgba(251,146,60,0.08)" : "rgba(34,211,238,0.08)",
    border: `1px solid ${held ? "rgba(251,146,60,0.25)" : "rgba(34,211,238,0.25)"}`,
  }),
  totalLabel: {
    fontSize: "0.62rem", color: "rgba(255,255,255,0.5)", textTransform: "uppercase",
    letterSpacing: "0.05em", fontWeight: 700,
  },
  totalValue: (held) => ({
    fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.2, whiteSpace: "nowrap",
    color: held ? "#fb923c" : "#22d3ee",
  }),
  /* table card */
  card: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "12px", marginTop: 14,
  },
  groupHead: (first) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 10px", marginTop: first ? 0 : 14, marginBottom: 6,
    borderRadius: 10, background: "rgba(34,211,238,0.08)",
    border: "1px solid rgba(34,211,238,0.25)",
  }),
  groupTitle: {
    fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase",
    color: "#22d3ee", fontWeight: 700,
  },
  groupCount: { fontSize: "0.68rem", color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap" },
  colHead: {
    fontSize: "0.62rem", letterSpacing: "0.05em", textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rowName: (held) => ({
    fontSize: "0.85rem", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis",
    color: held ? "rgba(255,255,255,0.35)" : "#22d3ee",
    textDecoration: held ? "line-through" : "none",
    textDecorationColor: "rgba(251,146,60,0.6)",
  }),
  numCell: (held) => ({
    fontSize: "0.84rem", textAlign: "right", whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis", fontVariantNumeric: "tabular-nums",
    color: held ? "rgba(255,255,255,0.35)" : "#e2e8f0",
    textDecoration: held ? "line-through" : "none",
    textDecorationColor: "rgba(251,146,60,0.6)",
  }),
  statusBadge: (held) => ({
    display: "inline-block", padding: "3px 10px", borderRadius: 999,
    fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap",
    ...(held
      ? { background: "rgba(251,146,60,0.15)", color: "#fb923c", border: "1px solid rgba(251,146,60,0.4)" }
      : { background: "rgba(34,211,238,0.15)", color: "#22d3ee", border: "1px solid rgba(34,211,238,0.4)" }),
  }),
  checkbox: (checked, disabled) => ({
    appearance: "none", WebkitAppearance: "none",
    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
    border: `1.5px solid ${checked ? "#fb923c" : "rgba(255,255,255,0.25)"}`,
    background: checked ? "rgba(251,146,60,0.2)" : "rgba(255,255,255,0.04)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.3 : 1,
    display: "inline-grid", placeContent: "center",
    transition: "all .15s ease",
  }),
  empty: {
    padding: "24px", textAlign: "center", color: "rgba(255,255,255,0.5)",
    fontStyle: "italic", fontSize: "0.85rem",
  },
  hint: {
    marginTop: 10, fontSize: "0.72rem", color: "rgba(255,255,255,0.4)",
    textAlign: "center",
  },
  footer: { display: "flex", gap: 10, marginTop: 12 },
  closeBtn: {
    flex: 1, padding: "11px", borderRadius: 10, border: "none",
    background: "rgba(255,255,255,0.06)", color: "#e2e8f0", fontWeight: 600,
    cursor: "pointer",
  },
  submitBtn: (disabled) => ({
    flex: 1, padding: "11px", borderRadius: 10, border: "none",
    background: disabled
      ? "rgba(251,146,60,0.15)"
      : "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
    color: disabled ? "rgba(255,255,255,0.35)" : "#0f172a",
    fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 4px 14px rgba(251,146,60,0.3)",
    transition: "opacity .12s ease",
  }),
};

/* checkbox | Business Name | Pay Status | PO Value | GST | Inc GST
 * (PO Num + Approved dropped so everything fits without horizontal scroll) */
const TABLE_COLS = "34px minmax(0,1.6fr) 0.9fr 0.8fr 0.8fr 0.8fr";

const HoldPaymentsModal = ({ rows, setToast, onClose, refetch }) => {
  const [held, setHeld] = useState(() => new Set()); // recordId values (index 9 — the subbie-table record ids)
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false); // Airtable sync settle window

  /* rows prop updates after each refetch (parent re-renders); the async
   * poll loop in onSubmit reads this ref to see the freshest data */
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [busy, onClose]);

  /* cyan totals exclude ticked rows; amber pills show what's been parked */
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          const bucket = held.has(r[IDX.recordId]) ? acc.held : acc.pay;
          bucket.poValue += parseCurrency(r[IDX.poValue]);
          bucket.gst += parseCurrency(r[IDX.gst]);
          bucket.incGst += parseCurrency(r[IDX.incGst]);
          return acc;
        },
        { pay: { poValue: 0, gst: 0, incGst: 0 }, held: { poValue: 0, gst: 0, incGst: 0 } }
      ),
    [rows, held]
  );

  /* group by End Of Month, keeping backend order within each group */
  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const key = r[IDX.endOfMonth] || "—";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    });
    return [...m.entries()];
  }, [rows]);

  const toggle = (recId) => {
    if (!recId || busy) return;
    setHeld((prev) => {
      const next = new Set(prev);
      if (next.has(recId)) next.delete(recId);
      else next.add(recId);
      return next;
    });
  };

  const onSubmit = async () => {
    if (held.size === 0) {
      setToast({ message: "Tick at least one record to hold, or press Close.", type: "error" });
      return;
    }
    try {
      setBusy(true);
      const heldIds = [...held];
      const { updated } = await submitAbnHold({ recordIds: heldIds });

      /* Records patched on the subbie source — now poll the synced PO table
       * until the held records actually vanish from the Payments data.
       * Deterministic: closes as soon as the sync lands, whether that's
       * 3 seconds or 20. */
      setSyncing(true);
      let cleared = false;
      for (let i = 0; i < MAX_POLLS; i++) {
        await sleep(POLL_INTERVAL_MS);
        await refetch();
        await sleep(300); // let the refetched rows propagate into props
        const stillThere = rowsRef.current.some((r) => heldIds.includes(r[IDX.recordId]));
        if (!stillThere) {
          cleared = true;
          break;
        }
      }

      setToast({
        message: cleared
          ? `${updated} record${updated === 1 ? "" : "s"} set to ${HPN_STATUS}. Bank file will now total ${fmt(totals.pay.incGst)} Inc GST.`
          : `${updated} record${updated === 1 ? "" : "s"} set to ${HPN_STATUS} at the source, but Airtable's sync is running slow — wait a moment and refresh before downloading the bank file.`,
        type: cleared ? "success" : "error",
      });
      onClose();
    } catch (e) {
      setToast({ message: e.message || "Failed to hold payments", type: "error" });
    } finally {
      setSyncing(false);
      setBusy(false);
    }
  };

  const renderRow = (r, i) => {
    const recId = r[IDX.recordId];
    const isHeld = held.has(recId);
    return (
      <div
        key={r[IDX.id] || i}
        className="hp-row"
        onClick={() => toggle(recId)}
        style={{
          display: "grid",
          gridTemplateColumns: TABLE_COLS,
          alignItems: "center",
          padding: "10px 8px",
          cursor: recId ? "pointer" : "default",
          borderRadius: 8,
          borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
          columnGap: 8,
          background: isHeld ? "rgba(251,146,60,0.06)" : "transparent",
        }}
        title={
          recId
            ? isHeld
              ? "Untick to add this payment back to the totals"
              : "Tick to hold this payment (HPN)"
            : "Missing recordId — cannot hold"
        }
      >
        <span onClick={(e) => e.stopPropagation()} style={{ display: "flex", justifyContent: "center" }}>
          <span style={{ position: "relative", display: "inline-grid", placeContent: "center" }}>
            <input
              type="checkbox"
              checked={isHeld}
              disabled={!recId || busy}
              onChange={() => toggle(recId)}
              style={S.checkbox(isHeld, !recId || busy)}
            />
            {isHeld && (
              <span
                style={{
                  position: "absolute", inset: 0, display: "grid", placeContent: "center",
                  pointerEvents: "none", color: "#fb923c", fontSize: "0.7rem", fontWeight: 800,
                }}
              >
                ✓
              </span>
            )}
          </span>
        </span>
        <span style={S.rowName(isHeld)}>{r[IDX.business] || "—"}</span>
        <span>
          <span style={S.statusBadge(isHeld)}>{isHeld ? HPN_STATUS : r[IDX.status]}</span>
        </span>
        <span style={S.numCell(isHeld)}>{r[IDX.poValue]}</span>
        <span style={S.numCell(isHeld)}>{r[IDX.gst]}</span>
        <span style={S.numCell(isHeld)}>{r[IDX.incGst]}</span>
      </div>
    );
  };

  return (
    <div style={S.backdrop} onClick={busy ? undefined : onClose}>
      <style>{`
        .hp-row { transition: background .12s ease; }
        .hp-row:hover { background: rgba(34,211,238,0.07); }
      `}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />

        <div style={S.head}>
          <h4 style={S.title}>Hold Payments (HPN)</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* Cyan totals — what the .aba file will pay */}
        <div style={{ ...S.eyebrow, margin: "0 2px 8px" }}>Bank file totals</div>
        <div style={S.totalsRow}>
          <div style={S.totalPill(false)}>
            <div style={S.totalLabel}>PO Value</div>
            <div style={S.totalValue(false)}>{fmt(totals.pay.poValue)}</div>
          </div>
          <div style={S.totalPill(false)}>
            <div style={S.totalLabel}>GST</div>
            <div style={S.totalValue(false)}>{fmt(totals.pay.gst)}</div>
          </div>
          <div style={S.totalPill(false)}>
            <div style={S.totalLabel}>Inc GST</div>
            <div style={S.totalValue(false)}>{fmt(totals.pay.incGst)}</div>
          </div>
        </div>

        {/* Amber totals — what's parked as HPN (only once something is ticked) */}
        {held.size > 0 && (
          <>
            <div style={{ ...S.eyebrow, margin: "6px 2px 8px", color: "#fb923c" }}>
              Held (HPN) — {held.size} record{held.size === 1 ? "" : "s"}
            </div>
            <div style={S.totalsRow}>
              <div style={S.totalPill(true)}>
                <div style={S.totalLabel}>PO Value</div>
                <div style={S.totalValue(true)}>{fmt(totals.held.poValue)}</div>
              </div>
              <div style={S.totalPill(true)}>
                <div style={S.totalLabel}>GST</div>
                <div style={S.totalValue(true)}>{fmt(totals.held.gst)}</div>
              </div>
              <div style={S.totalPill(true)}>
                <div style={S.totalLabel}>Inc GST</div>
                <div style={S.totalValue(true)}>{fmt(totals.held.incGst)}</div>
              </div>
            </div>
          </>
        )}

        {/* Grouped table */}
        <div style={S.card}>
          {rows.length === 0 ? (
            <div style={S.empty}>No records on the Payments chip for this month.</div>
          ) : (
            <div style={{ maxHeight: 340, overflowY: "auto" }}>
              {groups.map(([eom, groupRows], g) => (
                <div key={eom}>
                  <div style={S.groupHead(g === 0)}>
                    <span style={S.groupTitle}>{eom}</span>
                    <span style={S.groupCount}>
                      {groupRows.length} record{groupRows.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: TABLE_COLS,
                      alignItems: "end",
                      padding: "6px 8px 8px",
                      borderBottom: "1px solid rgba(255,255,255,0.12)",
                      columnGap: 8,
                    }}
                  >
                    <span style={{ ...S.colHead, textAlign: "center" }}>Hold</span>
                    <span style={S.colHead}>Business Name</span>
                    <span style={S.colHead}>Pay Status</span>
                    <span style={{ ...S.colHead, textAlign: "right" }}>PO Value</span>
                    <span style={{ ...S.colHead, textAlign: "right" }}>GST</span>
                    <span style={{ ...S.colHead, textAlign: "right" }}>Inc GST</span>
                  </div>

                  {groupRows.map(renderRow)}
                </div>
              ))}
            </div>
          )}

          <div style={S.hint}>
            Tick a row to hold it (HPN) — its amounts move from the cyan totals to the amber
            Held totals. Submit to apply, then generate the .aba file.
          </div>
        </div>

        <div style={S.footer}>
          <button type="button" style={S.closeBtn} onClick={onClose} disabled={busy}>
            Close
          </button>
          <button
            type="button"
            style={S.submitBtn(busy || held.size === 0)}
            onClick={onSubmit}
            disabled={busy || held.size === 0}
          >
            {syncing
              ? "Syncing with Database…"
              : busy
              ? "Submitting…"
              : held.size === 0
              ? "Tick records to hold"
              : `Hold ${held.size} Payment${held.size === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HoldPaymentsModal;